import i18n, {TKey, useTranslation} from '@/translations/i18n'
import {Controller, useForm} from 'react-hook-form'
import {ModalTitle} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {useMe} from '@features/user/queries/useMe'
import {raise} from '@utilities/helpers'
import {FC, Fragment} from 'react'
import {useChangeFreelancerStatus} from '@features/admin/queries/useChangeFreelancerStatus'
import {toast} from '@utilities/toast'
import {Spinner} from '@components/ui/spinner'
import {z} from 'zod'
import {UserStatus} from '@features/user/types'
import {zodResolver} from '@hookform/resolvers/zod'
import {TextArea} from '@components/commons/textarea'
import {Radio, RadioGroup} from '@components/commons/radio'
import {Dialog, DialogContent} from '@components/ui/dialog'

const ValidationSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.enum([UserStatus.Enum.deactivated, UserStatus.Enum.suspended]),
        reason: z.string().min(1, {message: i18n.t('errors:required')})
    }),

    z.object({
        status: z.enum([UserStatus.Enum.active_available, UserStatus.Enum.active_unavailable]),
        reason: z.string().optional()
    })
])
type ValidationSchema = z.infer<typeof ValidationSchema>

interface ChangeStatusModalProps {
    onClose: () => void
}

const STATUSES = [
    {
        value: UserStatus.Enum.active_available,
        label: 'impersonate_statuses:active_available',
        helperText: 'impersonate_statuses:active_available_info'
    },
    {
        value: UserStatus.Enum.active_unavailable,
        label: 'impersonate_statuses:active_unavailable',
        helperText: 'impersonate_statuses:active_unavailable_info'
    },
    {
        value: UserStatus.Enum.suspended,
        label: 'impersonate_statuses:suspended',
        helperText: 'impersonate_statuses:suspended_info'
    },
    {
        value: UserStatus.Enum.deactivated,
        label: 'impersonate_statuses:deactivated',
        helperText: 'impersonate_statuses:deactivated_info'
    }
] as const satisfies {value: UserStatus; label: TKey; helperText: TKey}[]
export const STATUS_NEEDS_REASON: UserStatus[] = [UserStatus.Enum.suspended, UserStatus.Enum.deactivated]
export const MAX_REASON_CHARACTERS = 500
export const ChangeStatusModal: FC<ChangeStatusModalProps> = ({onClose}) => {
    const {t} = useTranslation()

    const userMeQuery = useMe({throwOnError: true})
    const user = userMeQuery.data ?? raise('user is nullish')

    const changeStatusMutation = useChangeFreelancerStatus({
        photographerId: user.id,
        onSuccess: () => {
            toast.success(t('impersonate_statuses:status_changed_success'))
            onClose()
        }
    })
    const {
        handleSubmit,
        register,
        watch,
        control,
        formState: {isDirty, isValid}
    } = useForm<ValidationSchema>({
        resolver: zodResolver(ValidationSchema),
        defaultValues: {
            status: user.status,
            reason: ''
        }
    })
    const [reasonWatch, statusWatch] = watch(['reason', 'status'])

    const onSubmit = (values: ValidationSchema) => {
        const getCorrectPayload = () => {
            switch (values.status) {
                case UserStatus.Enum.suspended:
                    return {status: UserStatus.Enum.suspended, suspension_reason: values.reason}
                case UserStatus.Enum.deactivated:
                    return {status: UserStatus.Enum.deactivated, deactivation_reason: values.reason}
                default:
                    return {status: values.status}
            }
        }
        changeStatusMutation.mutate(getCorrectPayload())
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-108" showCloseButton={false}>
                <form className="p-4 flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
                    <ModalTitle>{t('impersonate_statuses:change_status')}</ModalTitle>
                    <Controller
                        name="status"
                        control={control}
                        render={({field}) => (
                            <RadioGroup
                                onValueChange={(value: string) => field.onChange(value)}
                                value={field.value !== undefined ? field.value.toString() : ''}
                                className="flex flex-col gap-4"
                                name="status"
                            >
                                {STATUSES.map(item => (
                                    <Fragment key={item.value}>
                                        <Radio
                                            rootClassName="[&>p]:text-sm [&>p]:font-medium [&_small]:text-xs"
                                            value={item.value}
                                            label={t(item.label)}
                                            helpText={t(item.helperText)}
                                            id={item.value}
                                        />

                                        {statusWatch == item.value && STATUS_NEEDS_REASON.includes(statusWatch) && (
                                            <TextArea
                                                label={t('impersonate_statuses:more_details_label')}
                                                placeholder={t('impersonate_statuses:more_details_placeholder')}
                                                maxLength={MAX_REASON_CHARACTERS}
                                                helpText={t('commons:x_characters_available', {
                                                    count: MAX_REASON_CHARACTERS - (reasonWatch?.length ?? 0)
                                                })}
                                                {...register('reason')}
                                            />
                                        )}
                                    </Fragment>
                                ))}
                            </RadioGroup>
                        )}
                    />

                    <div className="flex gap-2.5 justify-end">
                        <Button onClick={onClose} variant="tertiary" disabled={changeStatusMutation.isPending}>
                            {t('commons:cancel')}
                        </Button>
                        <Button type="submit" disabled={!isDirty || !isValid || changeStatusMutation.isPending}>
                            {t('commons:submit')}
                            {changeStatusMutation.isPending && <Spinner />}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}
